import { isValidRuMobilePhone, normalizeRuMobilePhone } from '@fixgarden/shared/phone';
import type { Fetcher, LeadErrorCode, LeadRequest, LeadResponse } from './types';

export class LeadSubmissionError extends Error {
  constructor(
    public readonly code: LeadErrorCode,
    public readonly status: number | null,
    public readonly retryable: boolean,
  ) {
    super(code);
    this.name = 'LeadSubmissionError';
  }
}

export interface LeadClientConfig {
  endpoint: string;
  fetcher?: Fetcher;
}

function responseError(status: number, body: unknown): LeadSubmissionError {
  const data = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const rawCode = typeof data.reason === 'string' ? data.reason : data.error;
  const code: LeadErrorCode =
    rawCode === 'invalid_phone' || rawCode === 'leads_not_configured'
      ? rawCode
      : 'request_rejected';
  return new LeadSubmissionError(code, status, status >= 500);
}

export class LeadClient {
  private readonly endpoint: string;
  private readonly fetcher: Fetcher;

  constructor(config: LeadClientConfig) {
    this.endpoint = config.endpoint.replace(/\/$/u, '');
    this.fetcher = config.fetcher ?? ((url, init) => fetch(url, init));
  }

  async submit(input: LeadRequest, idempotencyKey: string): Promise<LeadResponse> {
    if (!isValidRuMobilePhone(input.phone)) {
      throw new LeadSubmissionError('invalid_phone', null, false);
    }

    const payload: LeadRequest = { ...input, phone: normalizeRuMobilePhone(input.phone) };
    let response: Awaited<ReturnType<Fetcher>>;
    try {
      response = await this.fetcher(`${this.endpoint}/v1/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify(payload),
        credentials: 'omit',
      });
    } catch {
      throw new LeadSubmissionError('network_error', null, true);
    }

    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      if (!response.ok) throw responseError(response.status, null);
    }
    if (!response.ok) throw responseError(response.status, body);

    const data = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
    if (typeof data.lead_id !== 'string' || typeof data.phone_last4 !== 'string') {
      throw new LeadSubmissionError('invalid_response', response.status, true);
    }
    return { lead_id: data.lead_id, phone_last4: data.phone_last4 };
  }
}

export function createLeadClient(config: LeadClientConfig): LeadClient {
  return new LeadClient(config);
}
